import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { get, post, put, del } from '../../api';
import SuppliersForm from './SuppliersForm';
import ImportWizard from './ImportWizard';
import ExportButton from './ExportButton';
import './SuppliersTable.css';

const SuppliersTable = () => {
  const { user } = useContext(AuthContext);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(20);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name:asc');
  
  // UI state
  const [showForm, setShowForm] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchSuppliers();
      } else {
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, sortBy]);

  // Fetch suppliers on page load and page change
  useEffect(() => {
    fetchSuppliers();
  }, [currentPage]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(sortBy && { sort: sortBy })
      };

      const response = await get('/contacts/suppliers', { params });
      const data = response.data;
      
      setSuppliers(data.data || []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);

    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupplier = () => {
    setEditingSupplier(null);
    setShowForm(true);
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingSupplier(null);
  };

  const handleFormSuccess = (message) => {
    setSuccess(message);
    setShowForm(false);
    setEditingSupplier(null);
    fetchSuppliers();
    setTimeout(() => setSuccess(null), 5000);
  };


  const handleImportSuccess = (message) => {
    setSuccess(message);
    setShowImportWizard(false);
    fetchSuppliers();
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleExport = async () => {
    try {
      const params = {
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(sortBy && { sort: sortBy })
      };

      const response = await get('/contacts/suppliers/export', { 
        params,
        responseType: 'blob'
      });

      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `suppliers_${new Date().toISOString().slice(0, 10)}.csv`;

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Export completed successfully');
      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      console.error('Error exporting suppliers:', err);
      setError('Failed to export suppliers');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSelectSupplier = (supplierId) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId) 
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSuppliers.length === suppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(suppliers.map(s => s.supplierId));
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`Are you sure you want to delete "${supplier.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await del(`/contacts/suppliers/${supplier.supplierId}`);
      setSuccess('Supplier deleted successfully');
      fetchSuppliers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error deleting supplier:', err);
      setError('Failed to delete supplier');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleMassDelete = async () => {
    if (selectedSuppliers.length === 0) {
      setError('No suppliers selected for deletion');
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedSuppliers.length} selected supplier(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      await del('/contacts/suppliers/mass-delete', {
        data: { supplierIds: selectedSuppliers }
      });
      
      setSuccess(`${selectedSuppliers.length} suppliers deleted successfully`);
      setSelectedSuppliers([]);
      fetchSuppliers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error mass deleting suppliers:', err);
      setError('Failed to delete suppliers');
      setTimeout(() => setError(null), 5000);
    }
  };

  const canEdit = ['super', 'admin'].includes(user?.role);

  if (loading && suppliers.length === 0) {
    return (
      <div className="contacts-loading">
        <div>Loading suppliers...</div>
      </div>
    );
  }

  return (
    <div className="suppliers-table">
      {error && <div className="contacts-error">{error}</div>}
      {success && <div className="contacts-success">{success}</div>}

      {/* Header with actions */}
      <div className="table-header">
        <div className="header-left">
          <h2>Suppliers ({totalItems})</h2>
        </div>
        <div className="header-actions">
          {canEdit && (
            <>
              {selectedSuppliers.length > 0 && (
                <button 
                  className="btn btn-danger"
                  onClick={handleMassDelete}
                >
                  Delete Selected ({selectedSuppliers.length})
                </button>
              )}
              <button 
                className="btn btn-secondary"
                onClick={() => setShowImportWizard(true)}
              >
                Import CSV/XLSX
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreateSupplier}
              >
                Add Supplier
              </button>
            </>
          )}
          <ExportButton onExport={handleExport} />
          
          {/* Filters and search moved to header */}
          <div className="header-filters">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name:asc">Name A-Z</option>
              <option value="name:desc">Name Z-A</option>
              <option value="updatedAt:desc">Recently Updated</option>
              <option value="updatedAt:asc">Oldest Updated</option>
            </select>
          </div>
        </div>
      </div>


      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {canEdit && (
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedSuppliers.length === suppliers.length && suppliers.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              <th>Name</th>
              <th>Primary Contact</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Status</th>
              <th>Updated</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="empty-state">
                  <div className="contacts-empty">
                    <h3>No suppliers found</h3>
                    <p>Get started by adding your first supplier or importing from a CSV file.</p>
                    {canEdit && (
                      <div className="empty-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={handleCreateSupplier}
                        >
                          Add Supplier
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={() => setShowImportWizard(true)}
                        >
                          Import CSV
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              suppliers.map((supplier) => (
                <tr key={supplier.supplierId} className={supplier.status === 'archived' ? 'archived' : ''}>
                  {canEdit && (
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedSuppliers.includes(supplier.supplierId)}
                        onChange={() => handleSelectSupplier(supplier.supplierId)}
                      />
                    </td>
                  )}
                  <td className="name-cell">
                    <div className="supplier-name">{supplier.name}</div>
                  </td>
                  <td>{supplier.primaryContact?.name || '-'}</td>
                  <td>
                    {supplier.primaryContact?.phone ? (
                      <a href={`tel:${supplier.primaryContact.phone}`} className="phone-link">
                        {supplier.primaryContact.phone}
                      </a>
                    ) : '-'}
                  </td>
                  <td>
                    {supplier.primaryContact?.email ? (
                      <a href={`mailto:${supplier.primaryContact.email}`} className="email-link">
                        {supplier.primaryContact.email}
                      </a>
                    ) : '-'}
                  </td>
                  <td>
                    <span className={`status-badge ${supplier.status}`}>
                      {supplier.status}
                    </span>
                  </td>
                  <td>{new Date(supplier.updatedAt).toLocaleDateString()}</td>
                  <td className="actions-col">
                    <div className="action-buttons">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditSupplier(supplier)}
                      >
                        Edit
                      </button>
                      {canEdit && (
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteSupplier(supplier)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <SuppliersForm
          supplier={editingSupplier}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {showImportWizard && (
        <ImportWizard
          type="suppliers"
          onClose={() => setShowImportWizard(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default SuppliersTable;
